import { css } from "@emotion/react";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import SearchIcon from "@mui/icons-material/Search";
import { FieldGroupProps, useFieldGroup } from "../FieldGroup";
import { FieldProps } from "../FieldBase";
import { FormControl } from "../FormControl";
import { FormHelperText } from "../FieldBase/FormHelperText";
import { InputBase } from "../InputBase";
import { InputLabel } from "../InputLabel";
import { Paper } from "../Paper";
import { Popper } from "../Popper";
import { Triangle } from "../Icon/Triangle";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden";
import { dispatchChangeEvent } from "../@utils/dispatchChangeEvent";
import { useDitectInputTouched } from "../FieldBase/useDitectInputTouched";
import { useInputValue } from "../hooks/useInputValue";
import { useMergeRefs } from "../hooks/useMergeRefs";
import { useOnClickOutside } from "../hooks/useOnClickOutside";
import { useTheme } from "../theme";
import { getSearch } from "./useSearch";
import {
  SelectFieldInputWrapper,
  SelectFieldInputWrapperContext,
} from "./SelectFieldInputWrapper";
import type { Options as PopperjsOptions } from "@popperjs/core";

function ownerDocument(node: Node | null | undefined): Document {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  return node?.ownerDocument || document;
}

type TraversalFunction = (
  list: HTMLUListElement,
  item?: Element | null,
) => ChildNode | null;

const nextItem: TraversalFunction = (list, item) => {
  if (list === item) {
    return list.firstChild;
  }
  if (item?.nextElementSibling) {
    return item.nextElementSibling;
  }
  return list.firstChild;
};

const previousItem: TraversalFunction = (list, item) => {
  if (list === item) {
    return list.lastChild;
  }
  if (item?.previousElementSibling) {
    return item.previousElementSibling;
  }
  return list.lastChild;
};

const moveFocus = (
  list: HTMLUListElement,
  currentFocus: Element | null,
  traversalFunction: TraversalFunction,
) => {
  let nextFocus = traversalFunction(list, currentFocus) as HTMLElement | null;
  while (nextFocus) {
    if (
      currentFocus === null
        ? nextFocus === list.lastChild
        : nextFocus === currentFocus
    ) {
      break;
    }
    const disabledFocus =
      (nextFocus as HTMLInputElement).disabled ||
      nextFocus.getAttribute("aria-disabled") === "true";
    if (disabledFocus) {
      nextFocus = traversalFunction(list, nextFocus) as HTMLElement;
    } else {
      nextFocus.focus();
      break;
    }
  }
};

const SelectInnerSpinButton = ({
  open,
  onClick,
}: {
  open: boolean;
  onClick: () => void;
}) => {
  const {
    typography,
    transitions: { duration, easing },
  } = useTheme();
  return (
    <Triangle
      onClick={onClick}
      data-testid="SelectFieldSpinButton"
      css={[
        css`
          font-size: ${typography.body.fontSize}px;
          transition: ${duration.shorter} ${easing.easeInOut};
        `,
        open
          ? css`
              transform: rotateX(180deg);
            `
          : null,
      ]}
    />
  );
};

const SelectList = forwardRef<
  HTMLUListElement,
  React.ComponentPropsWithoutRef<"ul">
>((props, ref) => {
  return (
    <ul
      ref={ref}
      role="listbox"
      tabIndex={-1}
      css={css`
        box-sizing: border-box;
        min-height: ${42 * 3.5}px;
        max-height: ${42 * 7.5}px;
        padding: 0;
        margin: 0;
        overflow-x: hidden;
        overflow-y: auto;
        list-style: none;
        border-radius: inherit;
      `}
      {...props}
    />
  );
});

const SelectOption = (props: React.ComponentPropsWithoutRef<"li">) => {
  const theme = useTheme();
  return (
    <li
      role="option"
      // aria-selectedは親要素から設定するため、この値は上書きされる
      aria-selected={false}
      css={css`
        box-sizing: border-box;
        min-height: ${theme.spacing * 6}px;
        padding: 9px 12px;
        color: ${theme.palette.text.primary};
        cursor: pointer;
        background-color: ${theme.palette.background.paper};
        border-bottom: 1px solid ${theme.palette.gray[300]};
        outline: 0px;
        transition: ${theme.transitions.duration.shortest};

        ${css(theme.typography.body)}
        &:last-child {
          border-bottom: none;
        }
        &:hover,
        &:focus {
          background-color: ${theme.palette.action.hover};
        }
        &[aria-selected="true"] {
          background-color: ${theme.palette.action.selected};
        }
        &[aria-disabled="true"] {
          color: ${theme.palette.text.disabled};
          cursor: default;
        }
        &[aria-readonly="true"],
        &[aria-disabled="true"] {
          &:hover {
            background-color: ${theme.palette.background.paper};
          }
        }
      `}
      {...props}
    />
  );
};

export interface SelectOptionValue {
  value: string | undefined;
  label: string;
  keys?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export type RenderSelectOption<
  T extends SelectOptionValue = SelectOptionValue,
> = (props: T) => React.JSX.Element;

export type RenderSelectListPrepend<
  T extends SelectOptionValue = SelectOptionValue,
> = (context: {
  updateValue: (option: T) => void;
  setOpen: (open: boolean) => void;
  value: string | undefined;
  query: string;
  ListItem: React.ComponentType<React.ComponentPropsWithoutRef<"li">>;
}) => React.ReactNode;

export interface SelectFieldProps<
  T extends SelectOptionValue = SelectOptionValue,
>
  extends Pick<FieldProps<string>, "helperText" | "invalid">, FieldGroupProps {
  renderOption?: RenderSelectOption<T>;
  /**
   * @deprecated Use renderListPrepend instead
   */
  children?: React.ReactNode;
  /**
   * 見出し
   */
  label?: string;
  name?: string;
  defaultValue?: string;
  /**
   * valueの変更は、defaultValueが指定されていないときのみ反映されます。
   */
  value?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  id?: string;
  className?: string;
  /**
   * placeholder
   */
  placeholder?: string;
  /**
   * selecting data is required
   */
  required?: boolean;
  options?: T[];
  loadOptions?: SelectLoadOptions<T>;
  disabledInput?: boolean;
  readOnly?: boolean;
  /**
   * SelectFieldのOptionがクリックされた際に発火する関数。
   */
  onChange?: (value: T["value"], options: T) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  loading?: boolean;
  originalInputTestId?: string;
  pseudoInputTestId?: string;
  /**
   * オプションの表示にPortalを利用するかどうかを指定します。
   * このpropがtrueの場合、Portalが無効化されます。
   * また初期値がtrueであることに注意してください。
   * つまり通常、オプションはこのコンポーネントの子要素としてレンダリングされます。
   * @default true
   */
  disablePortal?: boolean;
  /**
   * 見かけ上のinput要素に対するrefを利用できます。
   * 選択された値を取得する場合などは、inputHasValueRefを利用してください。
   */
  inputRef?: React.Ref<HTMLInputElement>;
  /**
   * 選択された値を保持しているinput要素に対するrefを利用できます。
   * 見えているinput要素にフォーカスしたい場合などは、inputRefを利用してください。
   */
  inputHasValueRef?: React.Ref<HTMLInputElement>;
  /**
   * オプションリストの先頭に追加コンテンツを表示する。
   * childrenの代替として使用可能で、より柔軟な制御が可能です。
   * これを利用する場合、childrenは無視されます。
   */
  renderListPrepend?: RenderSelectListPrepend<T>;
}

export const filterSelectOptionsByQuery = <
  T extends SelectOptionValue = SelectOptionValue,
>(
  options: T[],
  query: string,
) => {
  const result = getSearch(
    options.map((option) => {
      return {
        ...option,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        keys: option.keys || [option.label],
        content: option.value,
      };
    }),
    query,
  );
  return result;
};

export type SelectLoadOptions<T extends SelectOptionValue = SelectOptionValue> =
  (args: {
    query: string;
    options?: T[];
    selectedOptions?: T[];
  }) => Promise<T[] | undefined>;

const defaultLoadOptions = async <
  T extends SelectOptionValue = SelectOptionValue,
>({
  query,
  options,
}: {
  query: string;
  options?: T[];
  // eslint-disable-next-line @typescript-eslint/require-await
}): Promise<T[] | undefined> => {
  if (options === undefined) {
    return undefined;
  }
  if (query === undefined) {
    return options;
  }
  const result = filterSelectOptionsByQuery(options, query);
  return result.length ? result : options;
};

const DefaultOption = <T extends SelectOptionValue>(
  props: T,
): React.JSX.Element => {
  return <span>{props.label}</span>;
};

export const SelectField = <T extends SelectOptionValue>(
  props: SelectFieldProps<T>,
) => {
  const {
    label,
    name,
    options: propOptions,
    loadOptions = defaultLoadOptions,
    disabledInput,
    onChange,
    onFocus,
    placeholder,
    required,
    id,
    value: _valueProp,
    defaultValue,
    disabled,
    children,
    helperText,
    invalid,
    loading: propsLoading,
    readOnly: propsReadOnly,
    style,
    className,
    originalInputTestId,
    pseudoInputTestId,
    disablePortal = true,
    renderOption = DefaultOption,
    inputRef,
    inputHasValueRef,
    renderListPrepend: RenderListPrepend,
  } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState<string>("");
  const selectedOptionsCache = useRef<T[] | undefined>(propOptions);
  const [options, setOptions] = useState(propOptions);

  useEffect(() => {
    void loadOptions({
      query,
      options: propOptions,
      selectedOptions: selectedOptionsCache.current,
    }).then((result) => {
      setOptions(result);
    });
  }, [propOptions, loadOptions, query]);

  const [value, setValue, isControlled] = useInputValue(props);

  const { loading, readOnly } = useFieldGroup({
    loading: propsLoading,
    readOnly: propsReadOnly,
    onLoaded: () => {
      if (isControlled === false) {
        setValue(defaultValue);
      }
    },
  });

  const displayValue = useMemo(() => {
    /* eslint-disable react-hooks/refs */
    return (
      selectedOptionsCache.current?.find(
        (option) => option.value === value,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      ) || options?.find((option) => option.value === value)
    )?.label;
  }, [options, value]);

  const listRef = useRef<HTMLUListElement>(null);
  useOnClickOutside([ref, listRef], () => {
    setOpen(false);
    setQuery("");
  });

  const pseudoInputRef = useRef<HTMLInputElement>(null);
  const pseudoInputRefs = useMergeRefs(inputRef, pseudoInputRef);
  const originalInputRef = useRef<HTMLInputElement>(null);
  const originalInputRefs = useMergeRefs(inputHasValueRef, originalInputRef);

  const updateValue = useCallback(
    (option: T) => {
      onChange?.(option.value, option);
      setValue(option.value);
      selectedOptionsCache.current = options;
      setQuery("");
      if (originalInputRef.current) {
        dispatchChangeEvent(originalInputRef.current, option.value);
      }
    },
    [onChange, options, setValue],
  );

  const resetValue = useCallback(() => {
    setValue(undefined);
    if (onChange) {
      const option =
        options?.find((option) => option.value === undefined) ??
        ({ value: undefined, label: "" } as T);
      onChange(undefined, option);
    }
  }, [onChange, options, setValue]);

  const [touched, inputProps] = useDitectInputTouched(onFocus);

  const popperOptions: Partial<PopperjsOptions> = useMemo(() => {
    return {
      modifiers: [
        disablePortal
          ? {
              name: "computeStyles",
              options: {
                roundOffsets: ({ y }: { y: number }) => ({
                  x: 0,
                  y: Math.round(y),
                }),
              },
            }
          : {},
        {
          name: "sameWidth",
          enabled: true,
          phase: "beforeWrite",
          requires: ["computeStyles"],
          fn: ({ state }) => {
            state.styles.popper.width = `${state.rects.reference.width}px`;
          },
          effect: ({ state }) => {
            state.elements.popper.style.width = `${
              (state.elements.reference as HTMLElement).offsetWidth
            }px`;
          },
        },
      ],
    };
  }, [disablePortal]);

  const internalInputId = useId();
  const inputId = id ?? internalInputId;

  // Auto fillによって値が後から更新されることを防ぐ
  const [autoFillPreventionKey, updateAutoFillPreventionKey] = useReducer(
    (prev: number) => prev + 1,
    0,
  );

  return (
    <FormControl style={style} className={className}>
      {label ? (
        <InputLabel
          htmlFor={inputId}
          aria-disabled={disabled}
          required={required}
          loading={loading}
        >
          {label}
        </InputLabel>
      ) : null}
      <VisuallyHidden>
        <input
          ref={originalInputRefs}
          type="text"
          data-testid={originalInputTestId}
          required={required}
          name={name}
          key={`${value}-${autoFillPreventionKey}`}
          defaultValue={value}
          autoCapitalize="none"
          autoComplete="off"
          onInput={(event) => {
            event.preventDefault();
            if (value === undefined) {
              // 選択肢が選択されていない状態で、値に選択肢と一致するものがある場合、その値に更新する
              const option = options?.find(
                (option) => option.value === event.currentTarget.value,
              );
              if (option) {
                updateValue(option);
              } else {
                // 一致する選択肢がない場合、Auto fillが適用されると困るため無効化する
                updateAutoFillPreventionKey();
              }
            } else {
              // このinput要素の値でonInputが発火するケースはAuto fillによる場合のみ、選択肢の選択では発火しない。
              // ここでkeyを更新することでauto fillによる値の上書きを、さらに上書きできる。
              updateAutoFillPreventionKey();
            }
          }}
          autoCorrect="off"
          spellCheck="false"
          tabIndex={-1}
        />
      </VisuallyHidden>

      <div ref={ref}>
        <SelectFieldInputWrapperContext.Provider
          value={!query ? displayValue : undefined}
        >
          <InputBase
            id={inputId}
            renderInputWrapper={SelectFieldInputWrapper}
            ref={pseudoInputRefs}
            data-testid={pseudoInputTestId}
            placeholder={placeholder}
            disabled={disabled}
            loading={loading}
            readOnly={readOnly}
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            onKeyDown={(event) => {
              const { key } = event;
              if (key === "Delete" || key === "Backspace") {
                setQuery((prev) => {
                  if (prev === "") {
                    resetValue();
                  }
                  return prev;
                });
              } else if (key === "ArrowDown") {
                event.preventDefault();
                const list = listRef.current;
                if (list) {
                  moveFocus(list, null, nextItem);
                }
              } else if (event.key.length === 1) {
                setOpen(true);
              }
            }}
            type="text"
            startAdornment={
              value ||
              disabledInput ||
              (value === undefined &&
                options?.some(
                  (option) => option.value === undefined,
                )) ? undefined : (
                <SearchIcon />
              )
            }
            endAdornment={
              <SelectInnerSpinButton
                open={open}
                onClick={() => {
                  setOpen((prev) => {
                    if (prev) {
                      return false;
                    }
                    pseudoInputRef.current?.focus();
                    return prev;
                  });
                }}
              />
            }
            data-invalid={invalid}
            value={query}
            onChange={
              disabledInput
                ? // @see https://github.com/matsuri-tech/matsuri-ui/issues/2451
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  () => {}
                : (event) => {
                    const value = event.currentTarget.value;
                    setQuery(value);
                  }
            }
            data-touched={inputProps["data-touched"]}
            onFocus={(event) => {
              inputProps.onFocus(event);
              if (readOnly !== true) {
                setOpen(true);
              }
            }}
            css={
              disabledInput
                ? css`
                    caret-color: transparent;
                    input {
                      cursor: pointer;
                    }
                  `
                : null
            }
          />
        </SelectFieldInputWrapperContext.Provider>

        <Popper
          disablePortal={disablePortal}
          anchorEl={ref.current}
          placement="bottom-start"
          open={open}
          options={popperOptions}
        >
          <div>
            <Paper>
              <SelectList
                ref={listRef}
                onKeyDown={(event) => {
                  const key = event.key;
                  const list = listRef.current;
                  if (list === null) {
                    return;
                  }
                  const currentFocus = ownerDocument(list).activeElement;
                  if (key === "ArrowDown") {
                    event.preventDefault();
                    moveFocus(list, currentFocus, nextItem);
                  } else if (key === "ArrowUp") {
                    event.preventDefault();
                    moveFocus(list, currentFocus, previousItem);
                  }
                }}
              >
                {
                  // renderListPrependが指定されている場合、childrenは無視する
                  RenderListPrepend ? (
                    <RenderListPrepend
                      updateValue={updateValue}
                      setOpen={setOpen}
                      value={value}
                      query={query}
                      ListItem={SelectOption}
                    />
                  ) : (
                    React.Children.toArray(children).map((child, i) => (
                      <SelectOption
                        tabIndex={-1}
                        aria-readonly={true}
                        key={`child-${i}`}
                      >
                        {child}
                      </SelectOption>
                    ))
                  )
                }
                {options === undefined ? (
                  <SelectOption aria-disabled={true} tabIndex={-1}>
                    Loading...
                  </SelectOption>
                ) : options.length === 0 ? (
                  <SelectOption aria-disabled={true} tabIndex={-1}>
                    No options
                  </SelectOption>
                ) : (
                  options.map((option, index) => {
                    const selected = value === option.value;
                    return (
                      <SelectOption
                        onKeyDown={(event) => {
                          const { key } = event;
                          if (key === "Enter") {
                            event.preventDefault();
                            event.currentTarget.click();
                            pseudoInputRef.current?.focus();
                            setOpen(false);
                          }
                        }}
                        aria-selected={selected}
                        /**
                         * 何も選択されていないときは、先頭のオプションに飛べるようにしておく
                         */
                        tabIndex={
                          (value === undefined ? index === 0 : selected)
                            ? 0
                            : -1
                        }
                        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                        key={option.value || `undefinedValue`}
                        onClick={() => {
                          updateValue(option);
                          setOpen(false);
                        }}
                      >
                        {renderOption(option)}
                      </SelectOption>
                    );
                  })
                )}
              </SelectList>
            </Paper>
          </div>
        </Popper>
      </div>
      <FormHelperText
        invalid={invalid}
        loading={loading}
        touched={touched}
        value={value}
      >
        {helperText}
      </FormHelperText>
    </FormControl>
  );
};
